import { useCallback, useEffect, useMemo, useState } from "react";

import {
  UserCheck01,
  UserPlus01,
  Users01,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Users";
import { useAuth } from "@hl/shared-features/lib/features/auth";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { useWatchlistAndFollowContext } from "@hl/shared-features/lib/features/watchlist-and-follow/WatchlistAndFollowContext";
import {
  useGetFollowersCountQuery,
  useGetFollowersQuery,
} from "@hl/shared-features/lib/features/watchlist-and-follow/follow/follow.graphql.generated";
import {
  ManageFollowModal,
  ManageFollowModalTabs,
} from "@hl/shared-features/lib/features/watchlist-and-follow/follow/modals/ManageFollowModal";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";

type Props = {
  walletAddress?: string | null;
};

export const UserFollowers = (props: Props) => {
  const enableFollows = useFeatureFlags(FEATURE_FLAGS.ENABLE_FOLLOWS);

  if (!enableFollows) {
    return null;
  }

  return <UserFollowersInner {...props} />;
};

const UserFollowersInner = (props: Props) => {
  const { walletAddress } = props;
  const auth = useAuth();
  const [showFollowModal, setShowFollowModal] = useState(false);

  const { isFollowing, follow } = useWatchlistAndFollowContext();
  const getFollowersCountQueryResult = useGetFollowersCountQuery({
    variables: {
      slug: walletAddress!,
    },
    skip: walletAddress == null,
  });

  // We need to get followers to display their avatars (3 max)
  const getFollowersQueryResult = useGetFollowersQuery({
    variables: {
      input: {
        walletAddress: walletAddress!,
        cursor: null,
        limit: 3,
      },
    },
    skip: walletAddress == null,
  });

  const followers = getFollowersQueryResult.data?.getFollowers;
  const followersCount = followers?.pageInfo.totalCount ?? 0;

  const isLoading =
    getFollowersCountQueryResult.loading ||
    getFollowersQueryResult.loading ||
    getFollowersCountQueryResult.error != null ||
    getFollowersQueryResult.error != null;

  const isMe =
    walletAddress != null &&
    auth.walletAddress != null &&
    auth.walletAddress.toLowerCase() === walletAddress.toLowerCase();

  const showFollowButton =
    walletAddress != null &&
    auth.authenticated &&
    auth.walletAddress?.toLowerCase() !== walletAddress.toLowerCase();

  const avatars = useMemo(() => {
    if (followers == null) return [];

    return followers.edges
      .map((follower) => {
        return follower.publicAccountSettings?.displayAvatar;
      })
      .filter((avatar) => avatar != null) as string[];
  }, [followers]);

  useEffect(() => {
    if (auth.authenticated) {
      getFollowersCountQueryResult.refetch();
      getFollowersQueryResult.refetch();
    }
  }, [
    auth.authenticated,
    getFollowersCountQueryResult.refetch,
    getFollowersQueryResult.refetch,
  ]);

  const following = useMemo(
    () => isFollowing(walletAddress ?? ""),
    [walletAddress, isFollowing]
  );

  const handleOnFollowClick = useCallback(async () => {
    if (auth.authenticated && walletAddress != null) {
      try {
        await follow(walletAddress, !following);
        await Promise.allSettled([
          getFollowersCountQueryResult.refetch(),
          getFollowersQueryResult.refetch(),
        ]);
      } catch (error) {
        console.error(error);
      }
    }
  }, [following, auth.authenticated, walletAddress, follow]);

  return (
    <>
      <Box>
        <Stack>
          <FollowerDisplay
            isMe={isMe}
            avatars={avatars}
            count={followersCount}
            loading={isLoading}
            isFollowing={following}
            showFollowButton={showFollowButton}
            onClick={() => {
              setShowFollowModal(true);
            }}
            onFollowClick={handleOnFollowClick}
          />
        </Stack>
      </Box>

      <ManageFollowModal
        opened={showFollowModal}
        onClose={async () => {
          setShowFollowModal(false);
          await Promise.allSettled([
            getFollowersCountQueryResult.refetch(),
            getFollowersQueryResult.refetch(),
          ]);
        }}
        walletAddress={walletAddress}
        initialTab={ManageFollowModalTabs.FOLLOWERS}
      />
    </>
  );
};

type FollowerDisplayProps = {
  isMe: boolean;
  avatars: string[];
  count: number;
  loading?: boolean;
  isFollowing?: boolean;
  showFollowButton?: boolean;
  onClick?: () => void;
  onFollowClick?: () => Promise<void>;
};

const FollowerDisplay = (props: FollowerDisplayProps) => {
  const {
    isMe,
    avatars = [],
    count = 0,
    loading = false,
    isFollowing = false,
    showFollowButton = true,
    onClick,
    onFollowClick,
  } = props;
  const { walletAddress, authenticated } = useAuth();
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const theme = useMantineTheme();

  const formattedCount = useMemo(() => {
    if (loading) return "Loading...";
    if (count === 1) return "1 follower";
    if (count === 0) {
      return isMe ? (
        <Group spacing={4}>
          <Users01 height={18} />
          <Text inherit span>
            My followers
          </Text>
        </Group>
      ) : (
        ""
      );
    }

    return (
      Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      })
        .format(count)
        .toLowerCase() + " followers"
    );
  }, [count, loading, isMe]);

  const followButton = useMemo(() => {
    if (!walletAddress) return null;
    if (!authenticated) return null;
    if (count === 0) {
      return (
        <Button
          leftIcon={isFollowing ? <UserCheck01 /> : <UserPlus01 />}
          color="secondaryButton"
          onClick={async () => {
            if (onFollowClick == null) return;
            if (isFollowLoading) return;
            setIsFollowLoading(true);

            await onFollowClick();

            setIsFollowLoading(false);
          }}
        >
          Follow
        </Button>
      );
    }

    return (
      <ActionIcon
        size={20}
        color="textButton"
        title={isFollowing ? "Following" : "Follow"}
        opacity={isFollowing ? 1 : 0.25}
        onClick={async () => {
          if (onFollowClick == null) return;
          if (isFollowLoading) return;
          setIsFollowLoading(true);

          await onFollowClick();

          setIsFollowLoading(false);
        }}
      >
        {isFollowing ? <UserCheck01 /> : <UserPlus01 />}
      </ActionIcon>
    );
  }, [
    count,
    isFollowing,
    isFollowLoading,
    walletAddress,
    authenticated,
    onFollowClick,
  ]);

  if (loading) {
    return (
      <Group spacing={6}>
        <Group spacing={0}>
          {[0, 1, 2].map((_, index) => {
            return (
              <Skeleton
                key={index}
                w={24}
                h={24}
                sx={{
                  overflow: "hidden",
                  borderRadius: "50%",
                  border: `1px solid ${theme.colors.primaryBackground[0]}`,
                  marginLeft: index === 0 ? 0 : -12,
                  zIndex: avatars.length - index,
                }}
              />
            );
          })}
        </Group>

        <Skeleton h={14} w={64} />
      </Group>
    );
  }

  return (
    <Group spacing={8}>
      <UnstyledButton onClick={onClick}>
        <Group spacing={6} h={24}>
          {avatars.length > 0 && (
            <Group spacing={0}>
              {avatars.map((avatar, index) => (
                <Box
                  key={index}
                  sx={{
                    overflow: "hidden",
                    borderRadius: "50%",
                    border: `1px solid ${theme.colors.primaryBackground[0]}`,
                    background: theme.colors.primaryBackground[0],
                    marginLeft: index === 0 ? 0 : -12,
                    zIndex: avatars.length - index,
                  }}
                >
                  <Image src={avatar} width={24} height={24} alt={avatar} />
                </Box>
              ))}
            </Group>
          )}
          <Text size={"sm"}>{formattedCount}</Text>
        </Group>
      </UnstyledButton>

      {showFollowButton && followButton}
    </Group>
  );
};
